/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { Fragment, PureComponent } from 'react'
import { FormattedDate } from 'react-intl'
import { capitalize } from 'lodash'

import { EuiText, withEuiTheme, EuiSpacer } from '@elastic/eui'
import type { WithEuiThemeProps } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiTable } from '@modules/cui/Table'
import CuiElasticConsumptionUnits from '@modules/cui/formatters/CuiElasticConsumptionUnits'
import type { CuiTableColumn } from '@modules/cui/Table'
import { isActiveItem } from '@modules/billing-lib/utils'
import type { LineItem } from '@modules/billing-api/customTypes'

import { shortDate } from '@/config/dates'

import LineItemDetails from '../LineItemDetails'
import LineItemStatus from '../LineItemStatus'

import type { SerializedStyles } from '@emotion/react'
import type { FilterState } from '..'
import type { ReactElement } from 'react'

interface Props {
  initialLoading?: boolean
  filter: FilterState
  rows: LineItem[]
}

class PrepaidsTable extends PureComponent<Props & WithEuiThemeProps> {
  render(): ReactElement {
    const { initialLoading, rows, filter } = this.props
    const gridColumns = this.getGridColumns()

    if (!initialLoading && rows.length === 0) {
      return (
        <EuiText size='s'>
          <FormattedMessage
            data-test-id={`empty-state-filter-${filter}`}
            id='billing-details-summary.empty-grid.no-active-balances'
            defaultMessage='<strong>You have no {filter} balances.</strong> <capitalize>{filter}</capitalize> balances will show up here.'
            values={{
              filter,
              capitalize: (content: string) => capitalize(content),
              strong: (content) => <strong>{content}</strong>,
            }}
          />
        </EuiText>
      )
    }

    return (
      <Fragment>
        <CuiTable<LineItem>
          getRowId={(item) => item.id}
          getRowTestSubj={(item) => `table-line-item-${item.id}`}
          initialLoading={initialLoading}
          rows={rows}
          columns={gridColumns.items}
          rowStyle={this.getRowStyle}
          initialSort={gridColumns.initialSort}
          initialSortDirection='asc'
        />
        {this.renderDepletedItemDisclaimer()}
      </Fragment>
    )
  }

  renderBalanceColumn(value: number): ReactElement {
    return (
      <EuiText size='s'>
        <CuiElasticConsumptionUnits unit='none' value={value} />
      </EuiText>
    )
  }

  renderDepletedItemDisclaimer = () => {
    if (this.props.rows.length === 0) {
      return null
    }

    const hasAnyDepletionTypeLineItems = this.props.rows.some(
      ({ end_type }) => end_type === 'depletion',
    )

    if (hasAnyDepletionTypeLineItems) {
      return (
        <Fragment>
          <EuiSpacer />
          <FormattedMessage
            id='depleted-line-item.disclaimer'
            defaultMessage='* This line item will expire/expired when its credits are depleted. No on-demand usage will be associated with this, and it will not be considered for discounting or invoicing purposes.'
          />
        </Fragment>
      )
    }

    return null
  }

  getRowStyle = (lineItem: LineItem): SerializedStyles => {
    const { theme } = this.props

    return css(
      !isActiveItem(lineItem) && {
        '.euiTableRowCell': {
          color: theme.euiTheme.colors.textDisabled,
        },
      },
    )
  }

  getDepletionDisclaimerAsterix = ({ end_type }: LineItem) => {
    if (end_type === 'depletion') {
      return <span>*</span>
    }

    return null
  }

  getGridColumns(): {
    initialSort: CuiTableColumn<LineItem>
    items: Array<CuiTableColumn<LineItem>>
  } {
    const expirationDateColumn: CuiTableColumn<LineItem> = {
      label: (
        <FormattedMessage
          id='prepaid-account-details.expiration-date'
          defaultMessage='Expiration date'
        />
      ),
      render: ({ end }) => (
        <FormattedDate data-test-id='expiration-date-column-value' value={end} {...shortDate} />
      ),
      sortKey: ({ end }) => new Date(end),
      width: '130px',
      truncateText: true,
    }

    return {
      initialSort: expirationDateColumn,
      items: [
        {
          label: <FormattedMessage id='prepaid-account-details.status' defaultMessage='Status' />,
          render: (line_item) => (
            <Fragment>
              <LineItemStatus lineItem={line_item} type='health' />
              {this.getDepletionDisclaimerAsterix(line_item)}
            </Fragment>
          ),
          truncateText: true,
        },
        {
          label: (
            <FormattedMessage
              id='prepaid-account-details.initial-balance'
              defaultMessage='Credits'
            />
          ),
          render: ({ ecu_quantity }) => this.renderBalanceColumn(ecu_quantity),
          align: 'right',
          truncateText: true,
        },
        {
          label: (
            <FormattedMessage id='prepaid-account-details.amount-spent' defaultMessage='Used' />
          ),
          render: ({ ecu_balance, ecu_quantity }) =>
            this.renderBalanceColumn(ecu_quantity - ecu_balance),
          align: 'right',
          truncateText: true,
        },
        {
          label: (
            <FormattedMessage
              id='prepaid-account-details.remaining-balance'
              defaultMessage='Remaining balance'
            />
          ),
          render: ({ ecu_balance }) => this.renderBalanceColumn(ecu_balance),
          align: 'right',
          truncateText: true,
        },
        expirationDateColumn,
        {
          label: (
            <FormattedMessage
              id='prepaid-account-details.contract-info'
              defaultMessage='Contract info'
            />
          ),
          render: (lineItem) => <LineItemDetails lineItem={lineItem} />,
          width: '100px',
          truncateText: true,
        },
      ],
    }
  }
}

export default withEuiTheme(PrepaidsTable)
